import React, { useState, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { SegmentedControl } from '../ui/segmented-control';
import { useConfirm } from '../ui/confirm';
import { useTheme } from '../../lib/theme';
import {
  SettingsSection,
  ToggleRow,
  InputRow,
  DangerSection,
  DangerRow
} from './SettingsUI';

const GeneralSettings = ({ config }) => {
  const { updateConfigSection } = useContext(AppContext);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [status, setStatus] = useState(null);
  const { apiRequest } = useAuth();
  const { preference: themePreference, setPreference: setThemePreference } = useTheme();
  const confirm = useConfirm();

  const [formData, setFormData] = useState({
    name: config.system?.name || 'Mirfa Home',
    notificationsEnabled: config.system?.notificationsEnabled !== false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);

    try {
      await updateConfigSection('system', formData);
      setStatus({ type: 'success', message: 'Settings saved.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRestart = async () => {
    const ok = await confirm({
      title: 'Restart Hub',
      description: 'Restart the hub? This will temporarily disconnect all services.',
      confirmText: 'Restart',
      destructive: true,
    });
    if (!ok) return;

    setRestarting(true);
    setStatus(null);

    try {
      const { data } = await apiRequest('/api/system/restart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (data.success) {
        setStatus({ type: 'success', message: 'Restarting...' });
        setTimeout(() => {
          setRestarting(false);
          window.location.reload();
        }, 5000);
      } else {
        throw new Error('Failed to restart');
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to restart.' });
      setRestarting(false);
    }
  };

  return (
    <div className="space-y-6">
      {status && (
        <Alert variant={status.type === 'error' ? 'destructive' : 'default'}>
          <AlertDescription>{status.message}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <SettingsSection title="Home">
          <InputRow
            label="Home Name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            required
          />
        </SettingsSection>

        <SettingsSection title="Notifications">
          <ToggleRow
            label="Enable Notifications"
            description="Show alerts for important events"
            checked={formData.notificationsEnabled}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notificationsEnabled: checked }))}
          />
        </SettingsSection>

        <Button type="submit" disabled={saving} className="w-full h-12 rounded-2xl">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </form>

      <SettingsSection title="Appearance">
        <div className="px-4 py-3">
          <p className="text-sm font-medium mb-2">Theme</p>
          <SegmentedControl
            options={[
              { label: 'System', value: 'system' },
              { label: 'Light', value: 'light' },
              { label: 'Dark', value: 'dark' },
            ]}
            value={themePreference}
            onChange={setThemePreference}
          />
        </div>
      </SettingsSection>

      <DangerSection>
        <DangerRow
          label="Restart Hub"
          description="Temporarily disconnects all services"
          buttonLabel={restarting ? 'Restarting...' : 'Restart'}
          onClick={handleRestart}
          loading={restarting}
        />
      </DangerSection>
    </div>
  );
};

export default GeneralSettings;
